import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape TEN-02 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { ConfigurationEntry } from "@tdelivr/api-client/generated/tenant/tenant-configuration.js";

import { asDay, boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { entryRow, filterEntries, ENTRY_STATES } from "../configuration.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Policies and settings, and Configuration: two pages of one listing.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, pages `policies` and `config`, against TEN-02
 * `GET /v1/configuration`.
 *
 * **One component for two pages, because the platform answers them with one operation.**
 * `tests/page-support.test.ts` measures which listing each page of this console is a board of, and
 * both of these come back `TEN-02 /v1/configuration`: both are boards of this tenant's
 * configuration entries. What separates them in the design is a third column — `Effective` on one,
 * `Release` on the other — and the listing can fill neither. Writing two components that fetched
 * the same operation and differed in one cell would be two places for the same decision.
 *
 * **It took both halves of what `page-support.test.ts` said was wrong.** The row shape:
 * `ConfigurationEntry` is now a published component, so `entries` carries seven named fields
 * instead of `type: object, additionalProperties: true`. The operation: typing the row alone would
 * have left these boards with nothing to ask for, because the namespace listing's namespace is a
 * required path segment behind a pattern and no operation anywhere publishes a vocabulary of
 * namespaces.
 *
 * **Both boards show every entry, and each says so.** The listing's only filter is `namespace`, a
 * string with no enumeration, so nothing published separates a policy from an operational setting.
 * A page that silently drew the same rows under a different title would teach an administrator that
 * one of the two was broken; a sentence under each grid is the whole of the fix. R-40.
 *
 * **`Effective` and `Release` are the two absences, and they are drawn differently on purpose.**
 * Q-101's recorded decision is that the policies column carries `updatedAt` **under its own
 * heading**: TEN-02 holds no scheduled effective date, and filling a column headed `Effective` from
 * `updatedAt` would show a September date against a change that takes effect in October — a
 * statement about an edit under a heading that asks about a policy, read by the published policies
 * page and by receipts. Q-104's is that the configuration column carries the measured absence: the
 * release machinery the whole page is about belongs to the application-configuration service and is
 * entirely under `/internal/v1`, which ADR 0029 §2 says the gateway refuses at load rather than
 * rewrites.
 */

const configurationApi = services.tenant__tenant_configuration;

/** Which of the two pages this is. The only thing that differs. */
export type ConfigurationVariant = "policies" | "config";

export interface ConfigurationBoardProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  readonly variant: ConfigurationVariant;
}

interface Loaded {
  readonly entries: readonly ConfigurationEntry[];
  /** TEN-02 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

export function ConfigurationBoard({
  transport,
  crumbs,
  variant,
}: ConfigurationBoardProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    const client = configurationApi.createTenantConfigurationClient(transport);
    try {
      /*
       * No `namespace`, which is the listing's one narrowing parameter.
       *
       * Sending one would mean choosing a namespace, and no operation on this platform publishes a
       * vocabulary of them — that is the whole reason this listing exists rather than the
       * by-namespace one. The board applies its state filter to what came back instead.
       */
      const answered = await client.listTenantConfiguration(undefined, PER_FETCH, undefined);
      setLoaded({
        entries: answered.entries,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["tenant-configuration:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return filterEntries(loaded.entries, state).map((entry) =>
      entryRow(
        entry,
        variant === "policies"
          ? {
              text: asDay(entry.updatedAt),
              title:
                "When this setting last changed, which is the only instant TEN-02 holds. It is not when the setting takes effect: see Q-101.",
            }
          : {
              text: "",
              absent: true,
              title:
                "The service that versions configuration releases publishes only internal operations, which the gateway refuses to route. Q-104.",
            },
      ),
    );
  }, [loaded, state, variant]);

  const policies = variant === "policies";

  const bounded = boundedNote(
    loaded?.entries.length,
    loaded?.truncated ?? false,
    "configuration entries",
    "ordered by namespace and key",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title={policies ? "Policies and settings" : "Configuration"}
      /*
       * `TEN-02`, where the configuration page's design chip reads `CFG-01 · TEN-02`.
       *
       * CFG-01 is the application-configuration service and every one of its operations is under
       * `/internal/v1`, so this page reads nothing from it. The brand board made the same call on
       * the same grounds: a chip naming a service the page cannot call tells an operator reporting
       * a problem to name the wrong one.
       */
      source="TEN-02"
      description={
        policies
          ? "Cancellation terms, support hours and the other settings that feed your published policy page."
          : /*
             * The design's second sentence is removed, not reworded, and this is the one place this
             * page's copy is not the design's.
             *
             * It reads "Releases are immutable and versioned: you change, review, publish, and can
             * roll back." Every part of that machinery exists and every operation of it is under
             * `/internal/v1`; ADR 0029 §2 closed the gateway route. So the sentence would describe
             * to a tenant administrator a workflow this page cannot offer them any step of, which
             * is the zones board's G-16 error — shipping a design-team annotation to a customer —
             * in its more expensive direction, because this one sounds like a feature. Q-104.
             */
            "Operational settings. Releases are not here: the service that versions them publishes no operation a browser can call."
      }
      columns={["Setting", "Value", policies ? "Last changed" : "Release"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Settings appear here as soon as one is written for this tenant."
      primary={{
        label: "Change setting",
        /*
         * Drawn, and refused. The design's form collects four fields and two have nothing on the
         * wire to write to: `Effective from` is a `datetime-local` and TEN-02 holds no effective
         * date (Q-101), and `Reason` is kept "in the audit log" — this service writes no audit
         * record and no reason field exists. `PUT /v1/configuration/{namespace}/{key}` takes a
         * `value` and an `expectedVersion`, and that is all it takes.
         *
         * The configuration page's form is worse again: its `Environment` select offers
         * development, staging and production, and its own subtitle says "Changes go into a draft
         * release. Nothing changes until it is published." There is no draft, no release and no
         * environment on TEN-02 — a write lands immediately.
         */
        refusal: policies
          ? "TEN-02's write takes a value and an expected version. There is nowhere to send the form's Effective from (Q-101) or its Reason, and a change would take effect immediately rather than on a date."
          : "TEN-02's write takes a value and an expected version, and lands immediately. There is no draft release to add to, no environment to choose and nowhere to send the form's Reason. Q-104.",
      }}
      filter={{
        unfiltered: "All states",
        options: ENTRY_STATES.map((value) => ({ value, label: value })),
        value: state,
        onChange: setState,
      }}
      note={
        policies
          ? {
              // The design's own `info` note, kept word for word. It is a statement about what
              // reads these values, and TEN-02 is the tenant's configuration store: the published
              // policies page and receipts reading it is the reason it exists.
              kind: "info",
              glyph: "info",
              text: "The published policies page and receipts read these values directly.",
            }
          : {
              /*
               * The design's `lock` note is replaced, and keeps its kind, its glyph and its place.
               *
               * It reads "Publishing a release to production needs a second person. Every release
               * keeps its full diff." Both halves are claims about a service this page cannot
               * reach, and the first is a safety claim — an administrator who believes a second
               * person is required will not look for one. Q-104.
               */
              kind: "lock",
              glyph: "lock",
              text: "A change here takes effect when it is written. TEN-02 keeps a version number per setting and no release, no diff and no approver.",
            }
      }
      {...(bounded === undefined ? {} : { bounded })}
      footnote={
        policies
          ? "Every configuration entry this tenant holds, across every namespace. Nothing TEN-02 publishes separates a policy from an operational setting, so the Configuration page is a board of the same entries."
          : "Every configuration entry this tenant holds, across every namespace. Nothing TEN-02 publishes separates an operational setting from a policy, so the Policies and settings page is a board of the same entries."
      }
      exportName={policies ? "policies" : "configuration"}
      detailSubtitle={policies ? "Policies and settings · TEN-02" : "Configuration · TEN-02"}
      detailNote={
        policies
          ? "The published policies page and receipts read these values directly."
          : "A change here takes effect when it is written. TEN-02 keeps a version number per setting and no release, no diff and no approver."
      }
    />
  );
}
